// Discovered attack. The line's first move steps a piece off a ray, and a
// slider of the same side behind it now attacks an enemy piece through the
// square it left: the slider did not attack that piece before the move and
// does after. What is uncovered names the variation: the enemy king makes
// it `check`, or `doubleCheck` when the piece that moved gives check as
// well; the queen or a rook make it `queen` or `rook`, provided the piece
// that moved makes a threat of its own (a capture, a check, or an attack on
// a piece it would win), since without one the uncovered piece simply
// moves away. Anything smaller uncovered is not named.
import type { Motif } from '@chess/api-mistakes';
import type { TacticLine } from './line.ts';
import { distance, isSlider, kingSquare, moved, piecesAlong, stepBetween, VALUE, winnable, type Placed } from './rays.ts';

export function discoveredAttack(line: TacticLine): Motif | undefined {
  const first = line.pv[0];
  const m = first ? moved(line.board, first) : undefined;
  if (!m) return undefined;
  const uncovered: Placed[] = [];
  for (const s of m.after.pieces()) {
    if (s.colour !== line.side || !isSlider(s.kind) || s.square === m.to) continue;
    const step = stepBetween(s.square, m.from);
    if (!step) continue;
    const [target] = piecesAlong(m.after, s.square, step, 1);
    if (!target || target.colour !== line.victim) continue;
    // Beyond the vacated square, and not attacked before the move.
    if (distance(s.square, target.square) <= distance(s.square, m.from)) continue;
    if (line.board.attacksFrom(s.square).includes(target.square)) continue;
    uncovered.push(target);
  }
  const target = uncovered.sort((a, b) => VALUE[b.kind] - VALUE[a.kind])[0];
  if (!target) return undefined;
  const king = kingSquare(m.after, line.victim);
  const moverAttacks = m.after.attacksFrom(m.to);
  const moverChecks = !!king && moverAttacks.includes(king);
  if (target.kind === 'king') return { family: 'discoveredAttack', variation: moverChecks ? 'doubleCheck' : 'check' };
  if (target.kind !== 'queen' && target.kind !== 'rook') return undefined;
  const threat =
    !!m.captured ||
    moverChecks ||
    moverAttacks.some((sq) => {
      const p = m.after.get(sq);
      return !!p && p.colour === line.victim && p.kind !== 'pawn' && winnable(m.after, sq, m.piece.kind);
    });
  return threat ? { family: 'discoveredAttack', variation: target.kind } : undefined;
}
